/**
 * Widget Input Components
 *
 * Replacement for MainComponents that don't depend on Bootstrap.
 * All classes use eg-* prefix for style isolation.
 */

import React from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import Select from 'react-select';

// Import date-fns locales for DatePicker
import { es, enUS, pt } from 'date-fns/locale';

// Register locales
registerLocale('es', es);
registerLocale('en', enUS);
registerLocale('pt', pt);

// Locale mapping
const dateLocales = {
  es: es,
  en: enUS,
  pt: pt
};

/**
 * Text/Textarea Input - replacement for MCInput
 */
export const EgInput = ({
  type = 'text',
  className = '',
  isValid = true,
  onChange = () => {},
  value,
  defaultValue,
  placeholder,
  name,
  rows,
  disabled,
  autoComplete = 'off',
  lang = 'es',
  ...rest
}) => {
  const invalidClass = !isValid ? 'eg-input-invalid' : '';
  const dateLocale = dateLocales[lang] || dateLocales.es;

  if (type === 'textarea') {
    return (
      <textarea
        className={`eg-input eg-textarea ${invalidClass} ${className}`}
        onChange={onChange}
        value={value}
        defaultValue={defaultValue}
        placeholder={placeholder}
        name={name}
        rows={rows || 3}
        disabled={disabled}
        autoComplete={autoComplete}
        {...rest}
      />
    );
  }

  if (type === 'date') {
    return (
      <DatePicker
        className={`eg-input eg-datepicker ${invalidClass} ${className}`}
        selected={value}
        dateFormat="dd/MM/yy"
        onChange={onChange}
        placeholderText={placeholder}
        disabled={disabled}
        locale={dateLocale}
        {...rest}
      />
    );
  }

  return (
    <input
      type={type}
      className={`eg-input ${invalidClass} ${className}`}
      onChange={onChange}
      value={value}
      defaultValue={defaultValue}
      placeholder={placeholder}
      name={name}
      disabled={disabled}
      autoComplete={autoComplete}
      {...rest}
    />
  );
};

/**
 * Checkbox Input
 */
export const EgCheckbox = ({
  className = '',
  onChange = () => {},
  checked,
  defaultChecked,
  name,
  id,
  disabled,
  ...rest
}) => {
  return (
    <input
      type="checkbox"
      className={`eg-checkbox ${className}`}
      onChange={onChange}
      checked={checked}
      defaultChecked={defaultChecked}
      name={name}
      id={id}
      disabled={disabled}
      {...rest}
    />
  );
};

/**
 * Radio Input
 */
export const EgRadio = ({
  className = '',
  onChange = () => {},
  checked,
  name,
  id,
  value,
  disabled,
  ...rest
}) => {
  return (
    <input
      type="radio"
      className={`eg-radio ${className}`}
      onChange={onChange}
      checked={checked}
      name={name}
      id={id}
      value={value}
      disabled={disabled}
      {...rest}
    />
  );
};

// SVG icons as base64 data URIs to avoid CSS reset issues
const CHEVRON_DOWN_ICON = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMCAyMCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIj48cGF0aCBkPSJNNC41MTYgNy41NDhjMC40MzYtMC40NDYgMS4wNDMtMC40ODEgMS41NzYgMGwzLjkwOCAzLjc0NyAzLjkwOC0zLjc0N2MwLjUzMy0wLjQ4MSAxLjE0MS0wLjQ0NiAxLjU3NCAwIDAuNDM2IDAuNDQ1IDAuNDA4IDEuMTk3IDAgMS42MTUtMC40MDYgMC40MTgtNC42OTUgNC41MDItNC42OTUgNC41MDItMC4yMTcgMC4yMjMtMC41MDIgMC4zMzUtMC43ODcgMC4zMzVzLTAuNTctMC4xMTItMC43ODktMC4zMzVjMCAwLTQuMjg3LTQuMDg0LTQuNjk1LTQuNTAycy0wLjQzNi0xLjE3IDAtMS42MTV6IiBmaWxsPSIjOGFhMWFjIi8+PC9zdmc+";
const CLEAR_ICON = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMCAyMCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIj48cGF0aCBkPSJNMTQuMzQ4IDE0Ljg0OWMtMC40NjkgMC40NjktMS4yMjkgMC40NjktMS42OTcgMGwtMi42NTEtMy4wMzAtMi42NTEgMy4wMjljLTAuNDY5IDAuNDY5LTEuMjI5IDAuNDY5LTEuNjk3IDAtMC40NjktMC40NjktMC40NjktMS4yMjkgMC0xLjY5N2wyLjc1OC0zLjE1LTIuNzU5LTMuMTUyYy0wLjQ2OS0wLjQ2OS0wLjQ2OS0xLjIyOCAwLTEuNjk3czEuMjI4LTAuNDY5IDEuNjk3IDBsMi42NTIgMy4wMzEgMi42NTEtMy4wMzFjMC40NjktMC40NjkgMS4yMjgtMC40NjkgMS42OTcgMHMwLjQ2OSAxLjIyOSAwIDEuNjk3bC0yLjc1OCAzLjE1MiAyLjc1OCAzLjE1YzAuNDY5IDAuNDY5IDAuNDY5IDEuMjI5IDAgMS42OTh6IiBmaWxsPSIjOGFhMWFjIi8+PC9zdmc+";

// Custom dropdown indicator using base64 image
const DropdownIndicator = (props) => {
  const { innerProps, selectProps } = props;
  const isOpen = selectProps.menuIsOpen;

  return (
    <div
      {...innerProps}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px',
        cursor: 'pointer',
        transition: 'transform 0.2s ease',
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
    >
      <img
        src={CHEVRON_DOWN_ICON}
        alt=""
        width="20"
        height="20"
        style={{
          display: 'block',
          width: '20px',
          height: '20px',
        }}
      />
    </div>
  );
};

// Custom clear indicator using base64 image
const ClearIndicator = (props) => {
  const { innerProps } = props;

  return (
    <div
      {...innerProps}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px',
        cursor: 'pointer',
      }}
    >
      <img
        src={CLEAR_ICON}
        alt=""
        width="20"
        height="20"
        style={{
          display: 'block',
          width: '20px',
          height: '20px',
        }}
      />
    </div>
  );
};

/**
 * Select Input - replacement for MCSelect
 *
 * Note: We DON'T use menuPortalTarget so the dropdown renders inside
 * the widget container where our styles are applied.
 */
export const EgSelect = ({
  options = [],
  value,
  onChange = () => {},
  placeholder,
  isDisabled,
  isSearchable = true,
  isClearable = false,
  className = '',
  theme = 'light',
  isValid = true,
  ...rest
}) => {
  const isDark = theme === 'dark';

  const customStyles = {
    container: (baseStyles) => ({
      ...baseStyles,
      // Ensure the container is positioned relative for the menu
      position: 'relative',
    }),
    control: (baseStyles, state) => ({
      ...baseStyles,
      border: `1px solid ${isValid ? (isDark ? '#363742' : '#bdcad0') : '#d81b60'}`,
      backgroundColor: isDark ? '#242836' : '#fff',
      borderRadius: '4px',
      minHeight: '42px',
      boxShadow: state.isFocused ? `0 0 0 2px rgba(0, 158, 215, 0.25)` : 'none',
      fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: '14px',
      '&:hover': {
        borderColor: '#009ed7'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected
        ? '#fff'
        : isDark ? '#bdcad0' : '#546e7a',
      backgroundColor: state.isSelected
        ? '#009ed7'
        : state.isFocused
          ? isDark ? '#32384d' : '#f3f7fa'
          : isDark ? '#242836' : '#fff',
      cursor: 'pointer',
      fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: '14px',
      padding: '10px 12px',
      '&:active': {
        backgroundColor: '#009ed7'
      }
    }),
    singleValue: (styles) => ({
      ...styles,
      color: isDark ? '#bdcad0' : '#333',
      fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: '14px',
    }),
    placeholder: (styles) => ({
      ...styles,
      color: isDark ? '#8aa1ac' : '#8aa1ac',
      fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: '14px',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDark ? '#242836' : '#fff',
      border: `1px solid ${isDark ? '#363742' : '#bdcad0'}`,
      borderRadius: '4px',
      zIndex: 9999,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      // Override any inherited styles
      fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: '14px',
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '4px 0',
    }),
    // Input container - holds the search input
    inputContainer: (styles, state) => ({
      ...styles,
      margin: '2px',
      padding: '0',
      color: isDark ? '#bdcad0' : '#333',
      flex: '1 1 auto',
      display: 'inline-grid',
      gridTemplateColumns: state.hasValue || state.selectProps.inputValue ? '0 minmax(min-content, 1fr)' : '0 100%',
    }),
    // The actual input element
    input: (styles, state) => ({
      ...styles,
      color: isDark ? '#bdcad0' : '#333',
      fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: '14px',
      margin: '0',
      padding: '0',
      opacity: 1,
      // Make input fill available space
      width: '100%',
      minWidth: state.selectProps.menuIsOpen ? '100%' : '2px',
      gridColumn: '1 / -1',
      background: 'transparent',
      border: 'none',
      outline: 'none',
    }),
    // Value container - holds selected value/placeholder and input
    valueContainer: (styles) => ({
      ...styles,
      padding: '2px 8px',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      flex: '1 1 0%',
      position: 'relative',
      overflow: 'hidden',
    }),
    // Indicators container (right side with arrow)
    indicatorsContainer: (styles) => ({
      ...styles,
      display: 'flex',
      alignItems: 'center',
      alignSelf: 'stretch',
      flexShrink: 0,
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    // Dropdown arrow indicator
    dropdownIndicator: (styles, state) => ({
      ...styles,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8px',
      color: isDark ? '#8aa1ac' : '#8aa1ac',
      cursor: 'pointer',
      transition: 'color 0.2s, transform 0.2s',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      '&:hover': {
        color: '#009ed7'
      },
      // Ensure SVG is visible
      '& svg': {
        display: 'block',
        width: '20px',
        height: '20px',
        fill: 'currentColor',
      }
    }),
    // Clear indicator (X button)
    clearIndicator: (styles) => ({
      ...styles,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8px',
      color: isDark ? '#8aa1ac' : '#8aa1ac',
      cursor: 'pointer',
      '&:hover': {
        color: '#d81b60'
      },
      '& svg': {
        display: 'block',
        width: '16px',
        height: '16px',
      }
    }),
  };

  return (
    <Select
      className={`eg-select ${className}`}
      classNamePrefix="eg-select"
      styles={customStyles}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder || 'Seleccionar...'}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      isClearable={isClearable}
      // Don't use menuPortalTarget - keep menu inside widget for style isolation
      menuPosition="absolute"
      // Use custom ASCII indicators instead of SVG
      components={{
        DropdownIndicator,
        ClearIndicator,
      }}
      {...rest}
    />
  );
};

// Note: EgButton is defined in WidgetGrid.jsx - use that one instead

/**
 * Label component
 */
export const EgLabel = ({
  children,
  required = false,
  className = '',
  htmlFor,
  ...props
}) => {
  return (
    <label
      className={`eg-label ${required ? 'eg-label-required' : ''} ${className}`}
      htmlFor={htmlFor}
      {...props}
    >
      {children}
      {required && ' *'}
    </label>
  );
};

/**
 * Form Group - container for label + input
 */
export const EgFormGroup = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <div className={`eg-form-group ${className}`} {...props}>
      {children}
    </div>
  );
};

/**
 * Error Message display
 */
export const EgErrorMessage = ({
  errors = [],
  show = true,
  className = ''
}) => {
  if (!show || !Array.isArray(errors) || errors.length === 0) {
    return null;
  }

  return (
    <div className={`eg-error-messages ${className}`}>
      {errors.map((error, idx) => (
        <div key={idx} className="eg-error-message">
          {error}
        </div>
      ))}
    </div>
  );
};

export default {
  EgInput,
  EgCheckbox,
  EgRadio,
  EgSelect,
  EgLabel,
  EgFormGroup,
  EgErrorMessage
};
